import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import tzlookup from 'tz-lookup';
import { ModalRef, SiModalService } from '@siemens/element-ng/modal';
import { PersonLocationAddresses } from '@sal/interfaces';
import { LocationErrorComponent } from '../components/location-error/location-error.component';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import type { ILocation } from '../../../shared/interfaces/ILocation';
import { ErrorService } from '../../../shared/services/error.service';

@Injectable({
    providedIn: 'root'
})
export class LocationService {
    private readonly modalService = inject(SiModalService);
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly errorService = inject(ErrorService);
    private locationErrorModalRef?: ModalRef<unknown, string>;

    public getLocationData(value: string): Promise<ILocation | null> {
        return this.fetchLocationData(value);
    }

    public getPersonLocationAddresses(salId: string, commonName: string): Observable<PersonLocationAddresses> {
        const encodedSalId = encodeURIComponent(salId);
        const encodedCommonName = encodeURIComponent(commonName);
        const url = `${environment.endpoint}/locations/${encodedSalId}/persons/${encodedCommonName}`;
        return this.http
            .get<PersonLocationAddresses>(url)
            .pipe(catchError(this.errorService.handleError<PersonLocationAddresses>('get person')));
    }

    public getTimeZoneFromCoordinates(latitude: number, longitude: number): string {
        return tzlookup(latitude, longitude);
    }

    public openDialog(): void {
        this.locationErrorModalRef?.hide('reopen');
        this.locationErrorModalRef = this.modalService.show(
            LocationErrorComponent,
            {
                ignoreBackdropClick: false,
                keyboard: true,
                animated: true,
                class: 'modal-dialog-centered',
                ariaLabelledBy: 'location-error-dialog-title'
            },
            'close'
        );

        this.locationErrorModalRef.hidden.subscribe(() => {
            this.locationErrorModalRef = undefined;
        });
    }

    private async fetchLocationData(value: string): Promise<ILocation | null> {
        try {
            const locationData = await firstValueFrom(
                this.http.get<ILocation>(`${environment.endpoint}/locations/${value}`)
            );

            if (!locationData) {
                this.openDialog();
                return null;
            }

            return locationData;
        } catch (err: unknown) {
            return this.handleLocationDataError(err, value);
        }
    }

    private handleLocationDataError(err: unknown, value: string): null {
        if (this.isForbiddenError(err)) {
            if (err.error.redirect) {
                window.location.href = `${err.error.redirect}/location/detail/${value}`;
            } else {
                void this.router.navigate(['/map']);
            }

            return null;
        }

        this.openDialog();
        return null;
    }

    private isForbiddenError(err: unknown): err is { status: number; error: { redirect?: string } } {
        return typeof err === 'object' && err !== null && 'status' in err && (err as { status: number }).status === 403;
    }
}
